import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, RefreshCw, LayoutDashboard, Home } from "lucide-react";
import { Button } from "../ui/button";

interface ActivityLoadErrorProps {
  errorType?: "prescription_not_found" | "process_load_failed" | "network_error" | "general_error";
  errorMessage?: string;
  activityName?: string;
  onRetry?: () => void;
}

const ActivityLoadError = ({
  errorType = "general_error",
  errorMessage,
  activityName,
  onRetry
}: ActivityLoadErrorProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getContent = () => {
    switch (errorType) {
      case "prescription_not_found":
        return {
          icon: <AlertCircle className="w-16 h-16 text-orange-500" />,
          title: t("activity_not_found") || "Activity Not Found",
          description:
            errorMessage ||
            t("prescription_not_found_description") ||
            "The activity you're trying to access could not be found. It may have been removed or the link may be incorrect.",
        };
      case "process_load_failed":
        return {
          icon: <AlertCircle className="w-16 h-16 text-red-500" />,
          title: t("failed_to_load_engagement") || "Failed to Load Engagement",
          description:
            errorMessage ||
            t("process_load_failed_description") ||
            "We couldn't load your engagement data. This might be a temporary issue.",
        };
      case "network_error":
        return {
          icon: <AlertCircle className="w-16 h-16 text-yellow-500" />,
          title: t("connection_error") || "Connection Error",
          description:
            errorMessage ||
            t("network_error_description") ||
            "We're having trouble connecting to the server. Please check your internet connection and try again.",
        };
      default:
        return {
          icon: <AlertCircle className="w-16 h-16 text-red-500" />,
          title: t("error_loading_activity") || "Error Loading Activity",
          description:
            errorMessage ||
            t("general_error_description") ||
            "Something went wrong while loading this activity. Please try again.",
        };
    }
  };

  const content = getContent();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">{content.icon}</div>

          {/* Title */}
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
            {content.title}
          </h1>

          {/* Activity Name (if available) */}
          {activityName && (
            <div className="mb-4 text-sm text-gray-600">
              <span className="font-medium">{t("activity")}:</span> {activityName}
            </div>
          )}

          {/* Description */}
          <p className="text-gray-600 mb-6 text-sm md:text-base">{content.description}</p>

          {/* Help Text */}
          <div className="bg-primary/5 border border-primary/20 rounded-md p-4 mb-6">
            <p className="text-sm text-primary">
              {t("error_help_text") || "If this problem persists, please try refreshing the page or contact support."}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {/* Retry Button (if onRetry is provided) */}
            {onRetry && (
              <Button
                onClick={onRetry}
                className="w-full min-h-[44px]"
                variant="default"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t("retry") || "Try Again"}
              </Button>
            )}

            {/* Go to Dashboard Button */}
            <Button
              onClick={() => navigate("/portal")}
              className="w-full min-h-[44px]"
              variant={onRetry ? "outline" : "default"}
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              {t("go_to_dashboard") || "Go to Dashboard"}
            </Button>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLoadError;
